import {useContext, useState} from 'react';
import {AxiosError} from 'axios';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAlertStore} from '@/store/alert_store';
import Helpers from '@/helpers/helpers';
import QueueMappingTable from '@/components/pages/settings/telephony/QueueMappingTable';
import QueueMappingForm from '@/components/pages/settings/telephony/QueueMappingForm';

export default function QueueMappingsTab() {
  const { carecoApi } = useContext(CarecoApiContext);
  const { addAlert } = useAlertStore();
  const queryClient = useQueryClient();

  const [isFormOpen, setIsFormOpen] = useState(false);

  const connectWorkspaceUrl = process.env.REACT_APP_CONNECT_WORKSPACE_URL;

  const { data: queueMappings = [], isLoading, error } = useQuery({
    queryKey: ['queueMappings'],
    queryFn: () => carecoApi?.getQueueMappings(),
    enabled: !!carecoApi,
  });

  const {
    mutate: createMapping,
    isPending: isCreating,
    error: createError,
    reset: resetCreateError,
  } = useMutation({
    mutationFn: async ({ rosterId, queueId, queueName }: { rosterId: number; queueId: string; queueName: string }) =>
      carecoApi?.createQueueMapping(rosterId, queueId, queueName),
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ['queueMappings'] });
      void queryClient.invalidateQueries({ queryKey: ['availableQueues'] });
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Queue mapping created successfully.',
      });
    },
    onError(error: AxiosError) {
      if (error.response?.status === 409) {
        return; // handled inline by the form
      }
      Helpers.alertOnError(`Failed to create queue mapping. ${Helpers.traceId(error)}`, error);
    },
  });

  const { mutate: deleteMapping, isPending: isDeleting } = useMutation({
    mutationFn: async ({ rosterId, queueId }: { rosterId: number; queueId: string }) =>
      carecoApi?.deleteQueueMapping(rosterId, queueId),
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ['queueMappings'] });
      void queryClient.invalidateQueries({ queryKey: ['availableQueues'] });
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Queue mapping removed successfully.',
      });
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to remove queue mapping. ${Helpers.traceId(error)}`, error);
    },
  });

  const handleOpenConnect = () => {
    if (connectWorkspaceUrl) {
      window.open(connectWorkspaceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSave = (rosterId: number, queueId: string, queueName: string) => {
    createMapping(
      { rosterId, queueId, queueName },
      { onSuccess: () => setIsFormOpen(false) },
    );
  };

  return (
    <>
      <QueueMappingTable
        queueMappings={queueMappings}
        isLoading={isLoading}
        error={error}
        onDelete={(rosterId, queueId) => deleteMapping({ rosterId, queueId })}
        isDeleting={isDeleting}
        onAddClick={() => setIsFormOpen(true)}
        connectWorkspaceUrl={connectWorkspaceUrl}
        onOpenConnect={handleOpenConnect}
      />
      <QueueMappingForm
        isOpen={isFormOpen}
        close={() => {
          setIsFormOpen(false);
          resetCreateError();
        }}
        onSave={handleSave}
        isSaving={isCreating}
        saveError={createError as AxiosError | null}
        existingMappings={queueMappings}
        resetError={resetCreateError}
      />
    </>
  );
}
